"use client";

import Link from "next/link";
import { useSyncExternalStore } from "react";
import { Home, Search } from "lucide-react";

export interface NotFoundStrings {
  title: string;
  description: string;
  hint: string;
  backHome: string;
  browseTemplates: string;
}

const isSpanishPath = (pathname: string | null) =>
  pathname === "/es" || (pathname?.startsWith("/es/") ?? false);

/**
 * The 404 body. Copy comes from the `NotFound` messages, and the two links have
 * to follow the locale: a Spanish 404 must not send visitors back to the
 * English homepage.
 */
export function NotFoundView({
  locale,
  strings,
}: {
  locale: "en" | "es";
  strings: NotFoundStrings;
}) {
  const homeHref = locale === "en" ? "/" : `/${locale}`;
  const templatesHref = locale === "en" ? "/templates" : `/${locale}/templates`;

  return (
    <main className="min-h-screen bg-white px-4 py-16 dark:bg-gray-900">
      <div className="mx-auto flex min-h-[calc(100vh-8rem)] max-w-2xl items-center justify-center">
        <div className="text-center">
          <div className="mb-8">
            <h1 className="bg-gradient-to-r from-blue-600 via-violet-600 to-blue-600 bg-clip-text text-9xl font-bold text-transparent md:text-[12rem]">
              404
            </h1>
          </div>

          <div className="mb-8">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 dark:text-gray-100 md:text-4xl">
              {strings.title}
            </h2>
            <p className="mb-2 text-lg text-gray-600 dark:text-gray-300">
              {strings.description}
            </p>
            <p className="text-base text-gray-500 dark:text-gray-400">
              {strings.hint}
            </p>
          </div>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href={homeHref}
              className="inline-flex items-center rounded-md bg-blue-600 px-6 py-3 text-white shadow-lg transition-all hover:bg-blue-700 hover:shadow-xl"
            >
              <Home className="mr-2 h-5 w-5" />
              {strings.backHome}
            </Link>
            <Link
              href={templatesHref}
              className="inline-flex items-center rounded-md border border-gray-300 px-6 py-3 text-gray-900 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800"
            >
              <Search className="mr-2 h-5 w-5" />
              {strings.browseTemplates}
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * 404 body for global-not-found, which is a static document rendered outside the
 * locale layout, so there is no locale provider to read. `useSyncExternalStore`
 * gives the server snapshot "en" (matching the prerendered document, so there is
 * no hydration mismatch) and then reads the locale from the URL on the client.
 * We can't set <html lang> from here - that stays "en".
 */
const subscribe = () => () => {};

export function GlobalNotFoundView({
  en,
  es,
}: {
  en: NotFoundStrings;
  es: NotFoundStrings;
}) {
  const locale = useSyncExternalStore(
    subscribe,
    () => (isSpanishPath(window.location.pathname) ? "es" : "en"),
    () => "en" as const,
  );

  return <NotFoundView locale={locale} strings={locale === "es" ? es : en} />;
}
